import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { toast } from 'react-toastify';
import jwtDecode, { JwtPayload } from 'jwt-decode';
import { Mutex } from 'async-mutex';
import { store } from '../redux/store';
import { API_URL } from '../constants';
import { JwtData } from '../types/misc';
import { logout as logoutDispatch } from '../redux/slices/applicationSlice';

const refreshMutex = new Mutex();

const httpService: AxiosInstance = axios.create({
  baseURL: API_URL,
});

export function getUserId(): number {
  const token = localStorage.getItem('token') ?? '';
  const data = jwtDecode<JwtData>(token);
  return data.payload.userId;
}

function logout(): void {
  store.dispatch(logoutDispatch());
  localStorage.clear();
}

async function refreshTokenIfNeeded(): Promise<void> {
  let accessTokenExp = localStorage.getItem('accessTokenExp');
  if (!accessTokenExp) {
    // for already logged in users when we don't have accessTokenExp yet
    const accessToken = localStorage.getItem('token');
    if (accessToken) {
      try {
        const decoded = jwtDecode<JwtPayload>(accessToken);
        if (decoded.exp) {
          localStorage.setItem('accessTokenExp', (decoded.exp * 1000).toString());
        }
      } catch {
        logout();
      }
      accessTokenExp = localStorage.getItem('accessTokenExp');
    }
  }

  // If there is an access token in the store and it has already expired/will expire in the next 20 seconds
  if (accessTokenExp && Date.now() + 20000 >= +accessTokenExp) {
    // Either try to refresh the token or wait for another request to do it
    if (!refreshMutex.isLocked()) {
      const release = await refreshMutex.acquire();
      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          const refreshResult = await axios.post(`${API_URL}/auth/refresh`, { refreshToken });

          if (refreshResult.data) {
            setToken(refreshResult.data.accessToken);
            setRefreshToken(refreshResult.data.refreshToken);
          } else {
            logout();
          }
        } else {
          logout();
        }
      } catch {
        logout();
      } finally {
        release();
      }
    } else {
      await refreshMutex.waitForUnlock();
    }
  }
}

export const attachToken = async (
  request: InternalAxiosRequestConfig,
): Promise<InternalAxiosRequestConfig> => {
  await refreshTokenIfNeeded();
  const token = localStorage.getItem('token');
  request.headers.Authorization = token ? `Bearer ${token}` : '';
  const { selectedCommunity } = store.getState().application;
  request.headers['x-community-id'] = selectedCommunity;
  request.headers['x-dashboard-version'] = 'COMMUNITY';
  return request;
};

export const attachVersion = (request: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  // eslint-disable-next-line global-require
  request.headers['App-Version'] = `moments-${require('../../package.json').version}`;
  return request;
};

export const attachNgrok = (request: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  if (process.env.REACT_APP_NGROK) {
    request.headers['ngrok-skip-browser-warning'] = 'true';
  }
  return request;
};

httpService.interceptors.request.use(attachToken);
httpService.interceptors.request.use(attachVersion);
httpService.interceptors.request.use(attachNgrok);

export const handleUnAuthorizedError = async (error: unknown): Promise<any> => {
  if (axios.isAxiosError(error)) {
    if (error.response?.status === 401) {
      const originalRequest = error.config;

      if (!originalRequest) return Promise.reject(error);
      await attachToken(originalRequest); // includes token refresh

      const accessToken = localStorage.getItem('token');
      if (!accessToken) return Promise.reject(error);

      try {
        return await httpService(originalRequest);
      } catch (retryError) {
        if (axios.isAxiosError(retryError) && retryError.response?.status === 401) {
          logout();
        }
        return Promise.reject(retryError);
      }
    }
  }
  return Promise.reject(error);
};

httpService.interceptors.response.use(null, handleUnAuthorizedError);

export const handleError = (error: any): Promise<void> => {
  if (!error.response.data.print) return Promise.reject(error);
  const errorMessage = error.response.data.error;

  toast.error(errorMap[errorMessage as keyof typeof errorMap] || errorMessage);

  return Promise.reject(error);
};

httpService.interceptors.response.use(null, handleError);

export const setToken = (token: string): void => {
  localStorage.setItem('token', token);
  const decoded = jwtDecode<JwtPayload>(token);
  if (decoded.exp) {
    localStorage.setItem('accessTokenExp', (decoded.exp * 1000).toString());
  }
};
export const setRefreshToken = (token: string): void => {
  localStorage.setItem('refreshToken', token);
};

export const wipeToken = (): void => {
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('accessTokenExp');
};

export default httpService;

const errorMap = {
  'Requested a new magic link too soon.':
    'Je hebt al een mail ontvangen. Probeer het later nog eens.',
  'There is no user with such primary email!': 'Gebruiker bestaat niet',
  'User already exists!': 'Gebruiker bestaat al',
  'Can not update invite when there are connections': 'Je hebt al connecties',
};
